import {
  Box,
  IconButton,
  keyframes,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { DASH_META_ROOT } from '@revelio/auth';

import {
  getDashMetaListenWebSocketUrl,
  getSupportedMimeType,
  parseFinalTranscript,
} from './speech-utils';

interface WindowWithWebkit extends Window {
  webkitAudioContext: typeof AudioContext;
}

export interface SpeechInputHandle {
  stopRecording: () => void;
}

interface SpeechInputProps {
  onTranscriptComplete: (transcript: string) => void;
  onEnter: () => void;
  onStartListening: () => void;
  textAreaRef: React.RefObject<HTMLTextAreaElement>;
  idleIconColor?: string;
}

const MEDIA_RECORDER_CHUNK_MS = 250;
const LISTEN_WARMUP_MS = 450;
const CONNECT_TIMEOUT_MS = 10000;
const MAX_RECORDING_MS = 30000;
const RECENT_TRANSCRIPT_DEDUP_MS = 3000;
const RECENT_TRANSCRIPT_LIMIT = 10;

type SpeechSession = {
  id: number;
  stream: MediaStream;
  socket: WebSocket | null;
  recorder: MediaRecorder | null;
  warmupTimeout: ReturnType<typeof setTimeout> | null;
  connectTimeout: ReturnType<typeof setTimeout> | null;
  maxRecordingTimeout: ReturnType<typeof setTimeout> | null;
  silenceTimeout: ReturnType<typeof setTimeout> | null;
  audioContext: AudioContext | null;
  audioSource: MediaStreamAudioSourceNode | null;
  analyser: AnalyserNode | null;
  dataArray: Uint8Array | null;
  animationFrame: number | null;
  lastSentTranscript: { text: string; at: number };
  recentTranscripts: Array<{ text: string; at: number }>;
  stopped: boolean;
  intentionalStop: boolean;
};

const listeningIconPulseAnimation = keyframes`
  0%, 100% { color: #C8E6C9; }
  50% { color: #2E7D32; }
`;

function AssistantMicIcon({ color }: { color: string }) {
  return (
    <Box
      as="svg"
      width="19px"
      height="19px"
      viewBox="0 0 19 19"
      fill="none"
      aria-hidden
    >
      <path
        d="M9.5 11.0833C11.1033 11.0833 12.4 9.78666 12.4 8.18333V5.28333C12.4 3.68 11.1033 2.38333 9.5 2.38333C7.89667 2.38333 6.6 3.68 6.6 5.28333V8.18333C6.6 9.78666 7.89667 11.0833 9.5 11.0833Z"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4.625 8.70834C4.625 11.3883 6.82 13.5833 9.5 13.5833C12.18 13.5833 14.375 11.3883 14.375 8.70834"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.5 13.5833V16.5"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.5 16.5H11.5"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Box>
  );
}

const SpeechInput = forwardRef<SpeechInputHandle, SpeechInputProps>(
  (
    {
      onTranscriptComplete,
      onEnter,
      onStartListening,
      textAreaRef,
      idleIconColor = '#2D426A',
    },
    ref
  ) => {
    const [listening, setListening] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const toast = useToast();

    const mountedRef = useRef(true);
    const listeningRef = useRef(false);
    const connectingRef = useRef(false);
    const startingRef = useRef(false);
    const nextSessionIdRef = useRef(0);
    const pendingStartIdRef = useRef<number | null>(null);
    const activeSessionRef = useRef<SpeechSession | null>(null);

    const setListeningState = useCallback((value: boolean) => {
      listeningRef.current = value;
      if (mountedRef.current) {
        setListening(value);
      }
    }, []);

    const setConnectingState = useCallback((value: boolean) => {
      connectingRef.current = value;
      if (mountedRef.current) {
        setConnecting(value);
      }
    }, []);

    const isCurrentSession = useCallback((session: SpeechSession) => {
      return (
        mountedRef.current &&
        activeSessionRef.current?.id === session.id &&
        !session.stopped
      );
    }, []);

    const cleanupSession = useCallback((session: SpeechSession) => {
      if (session.stopped) return;
      session.stopped = true;

      if (session.maxRecordingTimeout !== null) {
        clearTimeout(session.maxRecordingTimeout);
        session.maxRecordingTimeout = null;
      }
      if (session.connectTimeout !== null) {
        clearTimeout(session.connectTimeout);
        session.connectTimeout = null;
      }
      if (session.warmupTimeout !== null) {
        clearTimeout(session.warmupTimeout);
        session.warmupTimeout = null;
      }
      if (session.silenceTimeout !== null) {
        clearTimeout(session.silenceTimeout);
        session.silenceTimeout = null;
      }
      if (session.animationFrame !== null) {
        cancelAnimationFrame(session.animationFrame);
        session.animationFrame = null;
      }

      const recorder = session.recorder;
      if (recorder !== null) {
        recorder.ondataavailable = null;
        if (recorder.state !== 'inactive') {
          try {
            recorder.stop();
          } catch {
            // Recorder may already be stopped by the browser.
          }
        }
        session.recorder = null;
      }

      const socket = session.socket;
      if (socket !== null) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        if (
          socket.readyState !== WebSocket.CLOSED &&
          socket.readyState !== WebSocket.CLOSING
        ) {
          try {
            socket.close();
          } catch {
            // Socket may already be closing.
          }
        }
        session.socket = null;
      }

      session.audioSource?.disconnect();
      session.audioSource = null;
      session.analyser = null;
      session.dataArray = null;

      const audioContext = session.audioContext;
      if (audioContext !== null) {
        void audioContext.close().catch(() => {
          // AudioContext may already be closed or interrupted by the browser.
        });
        session.audioContext = null;
      }

      // biome-ignore lint/suspicious/useIterableCallbackReturn: track.stop() returns void
      session.stream.getTracks().forEach((track) => track.stop());
    }, []);

    const stopSession = useCallback(
      (
        sessionId: number,
        { intentional = true }: { intentional?: boolean } = {}
      ) => {
        const session = activeSessionRef.current;
        if (session === null || session.id !== sessionId) {
          return;
        }

        session.intentionalStop = intentional;
        cleanupSession(session);
        activeSessionRef.current = null;
        pendingStartIdRef.current = null;
        startingRef.current = false;
        setListeningState(false);
        setConnectingState(false);
      },
      [cleanupSession, setConnectingState, setListeningState]
    );

    const stopListening = useCallback(() => {
      pendingStartIdRef.current = null;
      startingRef.current = false;
      const session = activeSessionRef.current;
      if (session !== null) {
        stopSession(session.id, { intentional: true });
        return;
      }
      setListeningState(false);
      setConnectingState(false);
    }, [setConnectingState, setListeningState, stopSession]);

    const showSessionToast = useCallback(
      (session: SpeechSession, options: Parameters<typeof toast>[0]) => {
        if (!isCurrentSession(session) || session.intentionalStop) {
          return;
        }
        toast(options);
      },
      [isCurrentSession, toast]
    );

    const startAudioMonitoring = useCallback(
      (session: SpeechSession) => {
        const AudioContextCtor =
          window.AudioContext ||
          (window as unknown as WindowWithWebkit).webkitAudioContext;
        const ctx = new AudioContextCtor();
        session.audioContext = ctx;
        const source = ctx.createMediaStreamSource(session.stream);
        session.audioSource = source;
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        session.analyser = analyser;
        const bufferLength = analyser.frequencyBinCount;
        session.dataArray = new Uint8Array(bufferLength);
        source.connect(analyser);

        const threshold = 30;

        const checkVolume = () => {
          if (!isCurrentSession(session)) return;
          const dataArray = session.dataArray;
          const analyserNode = session.analyser;
          if (dataArray === null || analyserNode === null) return;

          analyserNode.getByteFrequencyData(
            dataArray as Uint8Array<ArrayBuffer>
          );
          const sum = dataArray.reduce((a, b) => a + b, 0);
          const average = sum / dataArray.length;
          const isCurrentlySpeaking = average > threshold;

          if (isCurrentlySpeaking && session.silenceTimeout !== null) {
            clearTimeout(session.silenceTimeout);
            session.silenceTimeout = null;
          } else if (
            !isCurrentlySpeaking &&
            listeningRef.current &&
            session.silenceTimeout === null
          ) {
            session.silenceTimeout = setTimeout(() => {
              session.silenceTimeout = null;
              if (!isCurrentSession(session)) return;
              if (textAreaRef?.current?.value?.length === 0) {
                showSessionToast(session, {
                  title: 'No sound detected',
                  description:
                    'Please check if your microphone is working properly',
                  status: 'error',
                  duration: 4000,
                  isClosable: true,
                  position: 'top-right',
                });
                stopSession(session.id, { intentional: false });
              }
            }, 3500);
          }

          session.animationFrame = requestAnimationFrame(checkVolume);
        };

        checkVolume();
      },
      [isCurrentSession, showSessionToast, stopSession, textAreaRef]
    );

    const shouldEmitTranscript = useCallback(
      (session: SpeechSession, text: string) => {
        const now = Date.now();
        session.recentTranscripts = session.recentTranscripts.filter(
          (entry) => now - entry.at < RECENT_TRANSCRIPT_DEDUP_MS
        );
        const hasRecentMatch = session.recentTranscripts.some(
          (entry) => entry.text === text
        );
        if (hasRecentMatch) {
          session.lastSentTranscript = { text, at: now };
          return false;
        }
        session.recentTranscripts = [
          ...session.recentTranscripts,
          { text, at: now },
        ].slice(-RECENT_TRANSCRIPT_LIMIT);
        return true;
      },
      []
    );

    useImperativeHandle(ref, () => ({ stopRecording: stopListening }), [
      stopListening,
    ]);

    const toggleListening = useCallback(async () => {
      if (
        listeningRef.current ||
        connectingRef.current ||
        startingRef.current ||
        activeSessionRef.current !== null
      ) {
        stopListening();
        return;
      }

      const startId = nextSessionIdRef.current + 1;
      nextSessionIdRef.current = startId;
      pendingStartIdRef.current = startId;
      startingRef.current = true;
      setConnectingState(true);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        if (
          !mountedRef.current ||
          pendingStartIdRef.current !== startId ||
          !startingRef.current
        ) {
          // biome-ignore lint/suspicious/useIterableCallbackReturn: track.stop() returns void
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        const listenUrl = getDashMetaListenWebSocketUrl(DASH_META_ROOT);

        const session: SpeechSession = {
          id: startId,
          stream,
          socket: null,
          recorder: null,
          warmupTimeout: null,
          connectTimeout: null,
          maxRecordingTimeout: null,
          silenceTimeout: null,
          audioContext: null,
          audioSource: null,
          analyser: null,
          dataArray: null,
          animationFrame: null,
          lastSentTranscript: { text: '', at: 0 },
          recentTranscripts: [],
          stopped: false,
          intentionalStop: false,
        };

        activeSessionRef.current = session;
        pendingStartIdRef.current = null;

        const socket = new WebSocket(listenUrl);
        session.socket = socket;

        session.connectTimeout = setTimeout(() => {
          session.connectTimeout = null;
          if (!isCurrentSession(session)) return;
          if (socket.readyState !== WebSocket.OPEN) {
            showSessionToast(session, {
              title: 'Speech connection timed out',
              description: 'Check that the server is running and try again.',
              status: 'error',
              duration: 4000,
              isClosable: true,
              position: 'top-right',
            });
            stopSession(session.id, { intentional: false });
          }
        }, CONNECT_TIMEOUT_MS);

        socket.onopen = () => {
          if (!isCurrentSession(session)) return;
          if (session.connectTimeout !== null) {
            clearTimeout(session.connectTimeout);
            session.connectTimeout = null;
          }
          session.warmupTimeout = setTimeout(() => {
            session.warmupTimeout = null;
            if (!isCurrentSession(session)) return;
            if (socket.readyState !== WebSocket.OPEN) return;
            session.lastSentTranscript = { text: '', at: 0 };
            session.recentTranscripts = [];
            const mimeType = getSupportedMimeType();
            const options: MediaRecorderOptions = {
              audioBitsPerSecond: 16000,
            };
            if (mimeType) {
              options.mimeType = mimeType;
            }
            const recorder = new MediaRecorder(stream, options);
            session.recorder = recorder;

            recorder.ondataavailable = (e) => {
              if (
                isCurrentSession(session) &&
                e.data.size > 0 &&
                socket.readyState === WebSocket.OPEN
              ) {
                socket.send(e.data);
              }
            };
            recorder.start(MEDIA_RECORDER_CHUNK_MS);
            startingRef.current = false;
            setListeningState(true);
            setConnectingState(false);
            onStartListening();
            startAudioMonitoring(session);
            session.maxRecordingTimeout = setTimeout(() => {
              session.maxRecordingTimeout = null;
              if (!isCurrentSession(session)) return;
              showSessionToast(session, {
                title: 'Recording limit reached',
                description: 'Stopped after 30 seconds to manage usage.',
                status: 'info',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
              });
              stopSession(session.id, { intentional: false });
            }, MAX_RECORDING_MS);
          }, LISTEN_WARMUP_MS);
        };

        socket.onmessage = async (event) => {
          if (!isCurrentSession(session)) return;
          const raw =
            typeof event.data === 'string'
              ? event.data
              : await (event.data as Blob).text();
          if (!isCurrentSession(session)) return;
          const result = parseFinalTranscript(raw, session.lastSentTranscript);
          if (
            result !== null &&
            shouldEmitTranscript(session, result.text) &&
            isCurrentSession(session)
          ) {
            session.lastSentTranscript = {
              text: result.text,
              at: Date.now(),
            };
            onTranscriptComplete(result.text);
          }
        };

        socket.onerror = () => {
          if (!isCurrentSession(session)) return;
          showSessionToast(session, {
            title: 'Speech connection error',
            description: 'Please try again',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
          stopSession(session.id, { intentional: false });
        };

        socket.onclose = () => {
          stopSession(session.id, { intentional: false });
        };
      } catch (err) {
        const startStillCurrent =
          pendingStartIdRef.current === startId ||
          activeSessionRef.current?.id === startId;
        pendingStartIdRef.current = null;
        startingRef.current = false;
        if (activeSessionRef.current?.id === startId) {
          stopSession(startId, { intentional: false });
        } else {
          setConnectingState(false);
        }
        if (!mountedRef.current || !startStillCurrent) {
          return;
        }
        console.error('Error starting speech input:', err);
        toast({
          title: 'Error accessing microphone',
          description:
            err instanceof Error
              ? err.message
              : 'Please check if your microphone is working properly',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      }
    }, [
      isCurrentSession,
      onStartListening,
      onTranscriptComplete,
      setConnectingState,
      setListeningState,
      shouldEmitTranscript,
      showSessionToast,
      startAudioMonitoring,
      stopListening,
      stopSession,
      toast,
    ]);

    React.useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
        stopListening();
      };
    }, [stopListening]);

    return (
      <Box display="flex" alignItems="center">
        <IconButton
          aria-label="Speech Input"
          icon={(() => {
            if (connecting) {
              return <Spinner size="sm" color="green.500" />;
            }
            if (listening) {
              return (
                <Box
                  as="span"
                  display="inline-block"
                  animation={`${listeningIconPulseAnimation} 1.2s ease-in-out infinite`}
                  color="green.300"
                >
                  <AssistantMicIcon color="currentColor" />
                </Box>
              );
            }
            return <AssistantMicIcon color={idleIconColor} />;
          })()}
          onClick={toggleListening}
          size="sm"
          variant="ghost"
          transition="all 0.2s ease-in-out"
          cursor="pointer"
          _hover={{}}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onEnter();
            }
          }}
        />
      </Box>
    );
  }
);

SpeechInput.displayName = 'SpeechInput';

export default SpeechInput;
